import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GridOn as GridOnIcon,
  Warehouse as WarehouseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import api from '../services/api.service';

export default function RackManager() {
  const [racks, setRacks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRack, setEditingRack] = useState(null);
  const [rackToDelete, setRackToDelete] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    warehouse_id: '',
    isActive: true
  });

  // Load racks and warehouses on mount
  useEffect(() => {
    loadRacks();
    loadWarehouses();
  }, []);

  const loadRacks = async () => {
    try {
      setLoading(true);
      setError(null);
      // Load racks from API
      const response = await api.get('/inventories/racks');
      console.log('[RACK MANAGER DEBUG] Racks API response:', response);
      const racksData = response.data?.data || response.data || [];
      console.log('[RACK MANAGER DEBUG] Extracted racks data:', racksData);
      setRacks(racksData);
    } catch (err) {
      setError('Failed to load racks');
      console.error('Error loading racks:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await api.get('/inventories/warehouses');
      const warehousesData = response.data?.data?.warehouses || response.data?.warehouses || [];
      setWarehouses(warehousesData);
    } catch (err) {
      console.error('Error loading warehouses:', err);
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddRack = () => {
    setEditingRack(null);
    setFormData({
      name: '',
      description: '',
      warehouse_id: warehouses.length > 0 ? warehouses[0].id : '',
      isActive: true
    });
    setDialogOpen(true);
  };

  const handleEditRack = (rack) => {
    setEditingRack(rack);
    setFormData({
      name: rack.name,
      description: rack.description || '',
      warehouse_id: rack.warehouse_id || (warehouses.length > 0 ? warehouses[0].id : ''),
      isActive: rack.isActive !== false
    });
    setDialogOpen(true);
  };

  const handleDeleteRack = (rack) => {
    setRackToDelete(rack);
    setDeleteDialogOpen(true);
  };

  const handleSaveRack = async () => {
    try {
      setError(null);
      
      if (!formData.name.trim()) {
        setError('Rack name is required');
        return;
      }
      
      if (!formData.warehouse_id) {
        setError('Please select a warehouse for this rack');
        return;
      }

      if (editingRack) {
        // Update existing rack
        await api.put(`/inventories/racks/${editingRack.id}`, formData);
        setSuccess('Rack updated successfully');
      } else {
        // Create new rack
        await api.post('/inventories/racks', formData);
        setSuccess('Rack created successfully');
      }
      
      setDialogOpen(false);
      loadRacks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save rack');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setError(null);
      await api.delete(`/inventories/racks/${rackToDelete.id}`);
      setSuccess('Rack deleted successfully');
      setDeleteDialogOpen(false);
      setRackToDelete(null);
      loadRacks();
    } catch (err) {
      console.log('[RACK DELETE ERROR] Full error response:', err.response);
      console.log('[RACK DELETE ERROR] Error data:', err.response?.data);
      console.log('[RACK DELETE ERROR] Error message:', err.response?.data?.error);
      
      // First try to get the specific error message from the API
      const errorMessage = err.response?.data?.error || err.response?.data?.message;
      
      if (errorMessage) {
        setError(errorMessage);
      } else if (err.response?.data?.itemsCount > 0) {
        setError(`Cannot delete rack: It has ${err.response.data.itemsCount} items. Please reassign items first.`);
      } else if (err.response?.data?.baysCount > 0) {
        setError(`Cannot delete rack: It has ${err.response.data.baysCount} bays. Please delete bays first.`);
      } else {
        setError(`Failed to delete rack. Status: ${err.response?.status || 'Unknown'}`);
      }
      setDeleteDialogOpen(false);
    }
  };

  const handleToggleActive = async (rack) => {
    try {
      await api.put(`/inventories/racks/${rack.id}`, {
        ...rack,
        isActive: !rack.isActive
      });
      loadRacks();
    } catch (err) {
      setError('Failed to update rack status');
    }
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GridOnIcon />
          Rack Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddRack}
        >
          Add Rack
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}


      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : racks.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <GridOnIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Racks Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create racks to organize inventory
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRack}
            >
              Create Rack
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Bays</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {racks
                .sort((a, b) => {
                  // Extract numeric part from rack names for proper sorting
                  const numA = parseInt(a.name.replace(/\D/g, '')) || 0;
                  const numB = parseInt(b.name.replace(/\D/g, '')) || 0;
                  return numA - numB;
                })
                .map((rack) => (
                <TableRow key={rack.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GridOnIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2">{rack.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {warehouses.find(w => w.id === rack.warehouse_id)?.name || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rack.isActive ? 'Active' : 'Inactive'}
                      color={rack.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={rack.baysCount || 0} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={rack.itemsCount || 0} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Toggle Active Status">
                      <Switch
                        checked={rack.isActive !== false}
                        onChange={() => handleToggleActive(rack)}
                        size="small"
                      />
                    </Tooltip>
                    <Tooltip title="Edit Rack">
                      <IconButton
                        size="small"
                        onClick={() => handleEditRack(rack)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Rack">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRack(rack)}
                        disabled={rack.itemsCount > 0}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Rack Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => {
          setDialogOpen(false);
          setEditingRack(null);
          setFormData({ name: '', isActive: true });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingRack ? 'Edit Rack' : 'Add New Rack'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              label="Rack Name"
              name="name"
              fullWidth
              value={formData.name}
              onChange={handleInputChange}
              required
              error={!formData.name && formData.name !== ''}
              helperText={!formData.name && formData.name !== '' ? 'Rack name is required' : ''}
            />

            <TextField
              label="Description"
              name="description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={handleInputChange}
            />

            <FormControl fullWidth required>
              <InputLabel>Warehouse</InputLabel>
              <Select
                name="warehouse_id"
                value={formData.warehouse_id}
                onChange={handleInputChange}
                label="Warehouse"
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false);
            setEditingRack(null);
            setFormData({ name: '', description: '', warehouse_id: '', isActive: true });
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveRack}
            variant="contained"
            disabled={!formData.name || !formData.warehouse_id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the rack "{rackToDelete?.name}"?
            {rackToDelete?.itemsCount > 0 && (
              <Box sx={{ mt: 1, color: 'error.main' }}>
                This rack contains {rackToDelete.itemsCount} items. You must reassign these items before deleting.
              </Box>
            )}
            {rackToDelete?.baysCount > 0 && (
              <Box sx={{ mt: 1, color: 'error.main' }}>
                This rack contains {rackToDelete.baysCount} bays. You must delete these bays first.
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={rackToDelete?.itemsCount > 0 || rackToDelete?.baysCount > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}